import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { Payments } from "@mui/icons-material";
import { updateTrainingGroupDefaultFee } from "./api";
import { getTrainingGroupId } from "./types";
import type { DefaultFeeBasis, TrainingGroup } from "./types";
import DialogHeader from "./DialogHeader";

interface GroupDefaultFeeDialogProps {
  target: { clubId: number; group: TrainingGroup } | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

const GroupDefaultFeeDialog: React.FC<GroupDefaultFeeDialogProps> = ({
  target,
  onClose,
  onSuccess,
  onError,
}) => {
  const [fee, setFee] = useState("0.00");
  const [basis, setBasis] = useState<DefaultFeeBasis>("invoice");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) {
      setFee((target.group.default_fee ?? 0).toFixed(2));
      setBasis(target.group.default_fee_basis ?? "invoice");
    }
  }, [target]);

  const handleSave = async () => {
    if (!target) return;
    const trainingGroupId = getTrainingGroupId(target.group);
    const parsedFee = parseFloat(fee || "0");
    if (trainingGroupId == null || Number.isNaN(parsedFee) || parsedFee < 0) return;

    try {
      setSaving(true);
      await updateTrainingGroupDefaultFee(trainingGroupId, parsedFee, basis);
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error updating group default fee:", err);
      onError("Failed to update default fee. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={target != null}
      onClose={() => !saving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogHeader
        title="Default Fee"
        subtitle={target?.group.group_name}
        icon={<Payments color="primary" />}
        onClose={onClose}
        closeDisabled={saving}
      />
      <DialogContent dividers>
        <TextField
          fullWidth
          label="Default amount"
          value={fee}
          onChange={(e) => setFee(e.target.value)}
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">£</InputAdornment>
            ),
          }}
          disabled={saving}
          sx={{ mt: 1 }}
          autoFocus
        />
        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Fee basis
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={basis}
          onChange={(_, value) => {
            if (value) setBasis(value as DefaultFeeBasis);
          }}
          disabled={saving}
          size="small"
        >
          <ToggleButton value="invoice">Per invoice</ToggleButton>
          <ToggleButton value="session">Per session</ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          {basis === "invoice"
            ? "Each member is invoiced this flat amount when you issue an invoice."
            : "Each member is invoiced this amount multiplied by the number of training sessions they are included in."}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || Number.isNaN(parseFloat(fee || "0"))}
          startIcon={
            saving ? <CircularProgress size={20} /> : <Payments />
          }
        >
          {saving ? "Saving…" : "Save Default Fee"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupDefaultFeeDialog;
